import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { Plus, X, GitCompare, AlertCircle, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import { INVESTMENT_TYPE_LABELS } from '../investments/investmentTableConstants';
import { useScopedPerformance } from './useScopedPerformance';
import { RANGE_OPTIONS } from './ScopedPerformanceChart';
import { formatCurrency } from './performanceUtils';
import { INITIAL_RC_DIM } from '../../utils/rechartsDefaults';
import { getToken } from '../../utils/tokenManager';
import logger from '../../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = 'compareSelection.v1';
const MAX_LINES = 5;

// Flat colour palette — no gradients, plain hex values. Distinct & accessible.
const LINE_COLOURS = ['#0EA5E9', '#F97316', '#10B981', '#A855F7', '#EF4444'];

const COMPARE_SCOPE_OPTIONS = [
  { value: 'investment', label: 'Investments' },
  { value: 'asset_type', label: 'Investment types' },
  { value: 'asset_class', label: 'Asset classes' },
];

const Y_AXIS_OPTIONS = [
  { value: 'return_pct', label: '% return from start', help: 'Each line starts at 0% on its first day. Fairest way to compare items of different sizes.' },
  { value: 'absolute', label: 'Dollar value', help: 'Plots each line in your home currency. Larger holdings will dwarf smaller ones.' },
];

const fmtDate = (s) => {
  if (!s) return '';
  const d = new Date(s);
  return isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

function loadSavedSelection() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Array.isArray(parsed.ids) && parsed.scope_type) return parsed;
  } catch (_e) { /* ignore */ }
  return null;
}

function CompareTooltip({ active, payload, label, yAxis }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-md p-2.5 shadow-md text-xs">
      <div className="text-slate-500 mb-1">{fmtDate(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2 py-0.5">
          <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: p.color }} />
          <span className="text-slate-700">{p.name}:</span>
          <span className="font-mono font-semibold text-slate-900">
            {yAxis === 'return_pct' ? `${Number(p.value).toFixed(2)}%` : formatCurrency(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

const ComparisonChart = () => {
  // Use the same scopes endpoint to populate the picker.
  const { scopes } = useScopedPerformance();

  const saved = loadSavedSelection();
  const [scopeType, setScopeType] = useState(saved?.scope_type || 'investment');
  const [selectedIds, setSelectedIds] = useState(saved?.ids || []);
  const [yAxis, setYAxis] = useState(saved?.y_axis || 'return_pct');
  const [range, setRange] = useState(saved?.range || 'all');

  const [series, setSeries] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');

  // Persist selection.
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ scope_type: scopeType, ids: selectedIds, y_axis: yAxis, range }));
    } catch (_e) { /* quota — ignore */ }
  }, [scopeType, selectedIds, yAxis, range]);

  // Whenever scope_type changes, clear the picked ids (they'd be from a different list).
  useEffect(() => {
    if (saved && saved.scope_type === scopeType) return; // initial load — keep saved ids
    setSelectedIds([]);
    setSearch('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeType]);

  const optionsForScope = useMemo(() => {
    if (scopeType === 'investment') {
      return (scopes.investments || []).map((inv) => ({
        id: inv.investment_id,
        label: inv.name,
        sub: INVESTMENT_TYPE_LABELS[inv.type] || inv.type,
      }));
    }
    if (scopeType === 'asset_type') {
      return (scopes.asset_types || []).map((t) => ({
        id: t,
        label: INVESTMENT_TYPE_LABELS[t] || t,
        sub: 'Investment type',
      }));
    }
    return (scopes.asset_classes || []).map((c) => ({
      id: c.value,
      label: c.label,
      sub: 'Asset class',
    }));
  }, [scopeType, scopes]);

  const labelById = useMemo(() => {
    const m = {};
    optionsForScope.forEach((o) => { m[o.id] = o.label; });
    return m;
  }, [optionsForScope]);

  const filteredOptions = useMemo(() => {
    const term = search.trim().toLowerCase();
    const available = optionsForScope.filter((o) => !selectedIds.includes(o.id));
    if (!term) return available;
    return available.filter((o) => o.label.toLowerCase().includes(term) || (o.sub || '').toLowerCase().includes(term));
  }, [optionsForScope, selectedIds, search]);

  const addId = (id) => {
    if (selectedIds.includes(id)) return;
    if (selectedIds.length >= MAX_LINES) {
      toast.info(`You can compare up to ${MAX_LINES} at a time. Remove one to add another.`);
      return;
    }
    setSelectedIds([...selectedIds, id]);
    setSearch('');
    setPickerOpen(false);
  };

  const removeId = (id) => setSelectedIds(selectedIds.filter((x) => x !== id));
  const clearAll = () => setSelectedIds([]);

  // Fetch comparison series whenever selection changes.
  const loadCompare = useCallback(async () => {
    if (selectedIds.length === 0) { setSeries([]); return; }
    setIsLoading(true);
    setError(null);
    try {
      const token = getToken();
      const r = await fetch(`${BACKEND_URL}/api/portfolio/performance/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ scope_type: scopeType, ids: selectedIds, y_axis: yAxis, range }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setSeries(j.series || []);
    } catch (e) {
      logger.error('Compare load failed:', e);
      setError(e);
      setSeries([]);
    } finally {
      setIsLoading(false);
    }
  }, [scopeType, selectedIds, yAxis, range]);

  useEffect(() => { loadCompare(); }, [loadCompare]);

  // Merge per-series data into a single recharts-ready array keyed by date.
  // Each line uses its own scope_id as the dataKey.
  const mergedData = useMemo(() => {
    if (!series.length) return [];
    const byDate = {};
    series.forEach((s) => {
      (s.data || []).forEach((pt) => {
        if (!byDate[pt.date]) byDate[pt.date] = { date: pt.date };
        byDate[pt.date][s.scope_id] = pt.value;
      });
    });
    return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
  }, [series]);

  const yAxisHelp = Y_AXIS_OPTIONS.find((o) => o.value === yAxis)?.help;

  return (
    <Card className="portfolio-card" data-testid="comparison-chart-card">
      <CardHeader className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <CardTitle className="text-base sm:text-xl font-serif flex items-center gap-2" data-testid="comparison-chart-title">
              <GitCompare className="w-4 h-4 text-slate-500" />
              Compare performance
            </CardTitle>
            <p className="text-xs text-slate-500 mt-1">
              Pick up to {MAX_LINES} {scopeType === 'investment' ? 'investments' : scopeType === 'asset_type' ? 'investment types' : 'asset classes'} to plot side by side.
            </p>
          </div>
          <div className="text-xs text-slate-500" data-testid="compare-chart-count-badge">
            {selectedIds.length} of {MAX_LINES} selected
          </div>
        </div>

        {/* Controls row: scope_type, y-axis, period range */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="compare-scope-type">Compare</label>
            <select
              id="compare-scope-type"
              data-testid="compare-scope-type-select"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
              value={scopeType}
              onChange={(e) => setScopeType(e.target.value)}
            >
              {COMPARE_SCOPE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="compare-yaxis">Y-axis</label>
            <select
              id="compare-yaxis"
              data-testid="compare-yaxis-select"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
              value={yAxis}
              onChange={(e) => setYAxis(e.target.value)}
            >
              {Y_AXIS_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            {yAxisHelp && <p className="text-[11px] text-slate-500 mt-1">{yAxisHelp}</p>}
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 block mb-1" htmlFor="compare-range">Period</label>
            <select
              id="compare-range"
              data-testid="compare-range-select"
              className="text-sm border border-slate-300 rounded-md px-2 py-1.5 bg-white text-slate-700 w-full"
              value={range}
              onChange={(e) => setRange(e.target.value)}
            >
              {RANGE_OPTIONS.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
          </div>
        </div>

        {/* Selection pills + add button */}
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2" data-testid="compare-pills-row">
            {selectedIds.map((id, idx) => (
              <span
                key={id}
                className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border"
                style={{ borderColor: LINE_COLOURS[idx % LINE_COLOURS.length], color: LINE_COLOURS[idx % LINE_COLOURS.length], background: 'white' }}
                data-testid={`compare-pill-${id}`}
              >
                <span className="inline-block w-2 h-2 rounded-sm" style={{ background: LINE_COLOURS[idx % LINE_COLOURS.length] }} />
                <span className="text-slate-700 max-w-[180px] truncate">{labelById[id] || id}</span>
                <button
                  type="button"
                  onClick={() => removeId(id)}
                  className="text-slate-400 hover:text-rose-600"
                  aria-label={`Remove ${labelById[id] || id}`}
                  data-testid={`compare-pill-remove-${id}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
            {selectedIds.length < MAX_LINES && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPickerOpen((v) => !v)}
                data-testid="compare-add-btn"
                className="text-xs h-7 px-2"
              >
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            )}
            {selectedIds.length > 0 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearAll}
                data-testid="compare-clear-btn"
                className="text-xs h-7 px-2 text-slate-500 hover:text-rose-600"
              >
                Clear all
              </Button>
            )}
          </div>

          {pickerOpen && (
            <div className="border border-slate-200 rounded-md bg-white shadow-xs p-2 max-h-60 overflow-y-auto" data-testid="compare-picker">
              <input
                type="text"
                placeholder={`Search ${scopeType === 'investment' ? 'investments' : scopeType === 'asset_type' ? 'types' : 'asset classes'}…`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm border border-slate-200 rounded px-2 py-1.5 mb-2"
                data-testid="compare-picker-search"
                autoFocus
              />
              {filteredOptions.length === 0 ? (
                <p className="text-xs text-slate-500 px-2 py-3 text-center">
                  {optionsForScope.length === 0 ? 'No options available yet.' : 'No matches.'}
                </p>
              ) : (
                <ul className="space-y-1">
                  {filteredOptions.slice(0, 50).map((o) => (
                    <li key={o.id}>
                      <button
                        type="button"
                        onClick={() => addId(o.id)}
                        className="w-full text-left flex items-center justify-between px-2 py-1.5 rounded hover:bg-slate-50"
                        data-testid={`compare-picker-option-${o.id}`}
                      >
                        <span className="text-sm text-slate-700">{o.label}</span>
                        <span className="text-[11px] text-slate-400 ml-2">{o.sub}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {selectedIds.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="compare-empty-prompt">
            <TrendingUp className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium">Nothing to compare yet</p>
            <p className="text-xs mt-1 max-w-md mx-auto">
              Press <span className="font-medium">Add</span> above to pick up to {MAX_LINES} items, then we&apos;ll plot them together on one chart.
            </p>
          </div>
        ) : isLoading ? (
          <div className="h-72 bg-slate-100 animate-pulse rounded" data-testid="compare-loading" />
        ) : error ? (
          <div className="text-center py-10 text-rose-600 flex flex-col items-center gap-2" data-testid="compare-error">
            <AlertCircle className="w-6 h-6" />
            <p className="text-sm">Could not load comparison data. Please retry.</p>
          </div>
        ) : mergedData.length === 0 ? (
          <div className="text-center py-12 text-slate-500" data-testid="compare-no-data">
            <TrendingUp className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-sm font-medium">No data yet for your selection</p>
            <p className="text-xs mt-1 max-w-md mx-auto">
              Comparison uses the same snapshots as your single-line chart. Press <span className="font-medium">Update Portfolio Value</span> above to record more points.
            </p>
          </div>
        ) : (
          <>
            <div className="h-72" data-testid="compare-chart" data-sensitive="chart">
              <ResponsiveContainer width="100%" height="100%" initialDimension={INITIAL_RC_DIM}>
                <LineChart data={mergedData} margin={{ top: 5, right: 12, left: 12, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="date" tickFormatter={fmtDate} stroke="#64748B" tick={{ fontSize: 11 }} />
                  <YAxis
                    stroke="#64748B"
                    tick={{ fontSize: 11 }}
                    width={70}
                    tickFormatter={(v) => yAxis === 'return_pct' ? `${Number(v).toFixed(0)}%` : formatCurrency(v).replace(/\.\d+/, '')}
                  />
                  <Tooltip content={<CompareTooltip yAxis={yAxis} />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {series.map((s, idx) => (
                    <Line
                      key={s.scope_id}
                      type="monotone"
                      dataKey={s.scope_id}
                      name={s.label}
                      stroke={LINE_COLOURS[idx % LINE_COLOURS.length]}
                      strokeWidth={2}
                      dot={{ r: 2.5 }}
                      activeDot={{ r: 4.5 }}
                      connectNulls
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Per-line "started at" notes — only show series whose first date is later than the earliest one */}
            {(() => {
              const datedSeries = series.filter((s) => s.started_at);
              if (datedSeries.length < 2) return null;
              const earliest = datedSeries.reduce((min, s) => (s.started_at < min ? s.started_at : min), datedSeries[0].started_at);
              const late = datedSeries.filter((s) => s.started_at && s.started_at > earliest);
              if (late.length === 0) return null;
              return (
                <div className="mt-3 text-[11px] text-slate-500 space-y-0.5" data-testid="compare-late-start-notes">
                  {late.map((s) => (
                    <div key={s.scope_id}>
                      <span className="font-medium text-slate-700">{s.label}</span> — tracking started {fmtDate(s.started_at)}.
                    </div>
                  ))}
                </div>
              );
            })()}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ComparisonChart;
